// TIBECO - Managementul Furnizorilor
import React, { useState } from 'react';
import { 
  Search,
  Filter,
  Plus,
  Star,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  Package,
  Award,
  Clock,
  TrendingUp,
  Eye
} from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const SuppliersManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('toate');
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Date furnizori TIBECO
  const suppliers = [
    {
      id: 1,
      nume: 'REHAU România SRL',
      categorie: 'Profile PVC',
      persoanaContact: 'Maria Popescu',
      telefon: '+40 21 234 5678',
      telefonContact: '+40 21 234 5679',
      email: 'comenzi@rehau.ro',
      adresa: 'Șos. Bucuresti-Ploiești 42-44, București',
      rating: 4.8,
      statusColaborare: 'Activ',
      valoareTotalaAn: 245000,
      numarComenzi: 24,
      dataUltimaComanda: '2024-09-01',
      valoareUltimaComanda: 12500,
      timpMediuLivrare: 7,
      rataIntarziere: 2,
      conditiiPlata: '30 zile',
      discount: '8%',
      produsePrincipale: ['Profile PVC', 'Accesorii PVC', 'Garnituri'],
      certificari: ['ISO 9001', 'RAL', 'CE'],
      note: 'Furnizor premium cu livrări rapide și calitate excelentă.'
    },
    {
      id: 2,
      nume: 'VEKA România',
      categorie: 'Profile PVC',
      persoanaContact: 'Ion Marinescu',
      telefon: '+40 21 345 6789',
      telefonContact: '+40 21 345 6790',
      email: 'office@veka.ro',
      adresa: 'Str. Industriei 15, Cluj-Napoca',
      rating: 4.6,
      statusColaborare: 'Activ',
      valoareTotalaAn: 189000,
      numarComenzi: 18,
      dataUltimaComanda: '2024-08-28',
      valoareUltimaComanda: 8900,
      timpMediuLivrare: 10,
      rataIntarziere: 5,
      conditiiPlata: '45 zile',
      discount: '5%',
      produsePrincipale: ['Profile PVC', 'Sisteme glisante', 'Accesorii'],
      certificari: ['ISO 9001', 'ISO 14001', 'CE'],
      note: 'Furnizor de încredere cu gamă largă de produse.'
    },
    {
      id: 3,
      nume: 'Guardian Glass România',
      categorie: 'Sticle',
      persoanaContact: 'Ana Gheorghe',
      telefon: '+40 31 456 7890',
      telefonContact: '+40 31 456 7891',
      email: 'comenzi@guardian.ro',
      adresa: 'Zona Industrială Sud, Ploiești',
      rating: 4.5,
      statusColaborare: 'Activ',
      valoareTotalaAn: 156000,
      numarComenzi: 32,
      dataUltimaComanda: '2024-09-02',
      valoareUltimaComanda: 4200,
      timpMediuLivrare: 14,
      rataIntarziere: 8,
      conditiiPlata: '30 zile',
      discount: '3%',
      produsePrincipale: ['Sticlă termopan', 'Sticlă laminată', 'Sticlă securizată'],
      certificari: ['CE', 'ISO 9001', 'EN 14449'],
      note: 'Specialist în sticle pentru ferestre și uși.'
    }
  ];

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.nume.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.categorie.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'toate' || supplier.categorie === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Activ': return 'success';
      case 'Inactiv': return 'danger';
      case 'Suspendat': return 'warning';
      default: return 'secondary';
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating)
            ? 'text-yellow-400 fill-current'
            : i < rating
            ? 'text-yellow-400 fill-current'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const handleViewSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setShowSupplierModal(true);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Managementul Furnizorilor
        </h1>
        <button className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors">
          <Plus className="w-4 h-4" />
          <span>Furnizor Nou</span>
        </button>
      </div>

      {/* Filtre și Căutare */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Caută furnizori..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="toate">Toate categoriile</option>
          <option value="Profile PVC">Profile PVC</option>
          <option value="Sticle">Sticle</option>
          <option value="Accesorii">Accesorii</option>
          <option value="Profile Aluminiu">Profile Aluminiu</option>
        </select>
      </div>

      {/* Lista Furnizori */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredSuppliers.map((supplier) => (
          <Card key={supplier.id} className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {supplier.nume}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {supplier.categorie}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex items-center">
                  {renderStars(supplier.rating)}
                </div>
                <Badge variant={getStatusColor(supplier.statusColaborare)}>
                  {supplier.statusColaborare}
                </Badge>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {supplier.telefon}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {supplier.email}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Package className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {supplier.persoanaContact}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {supplier.valoareTotalaAn.toLocaleString()}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  RON (2024)
                </div>
              </div>
              <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {supplier.numarComenzi}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Comenzi
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Ultima comandă: {new Date(supplier.dataUltimaComanda).toLocaleDateString('ro-RO')}
              </div>
              <button
                onClick={() => handleViewSupplier(supplier)}
                className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="text-sm">Detalii</span>
              </button>
            </div>
          </Card>
        ))}
      </div>

      {/* Modal Detalii Furnizor */}
      <Modal
        isOpen={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        title="Detalii Furnizor"
        size="xl"
      >
        {selectedSupplier && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {selectedSupplier.nume}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  {selectedSupplier.categorie} • {selectedSupplier.persoanaContact}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex items-center">
                  {renderStars(selectedSupplier.rating)}
                </div>
                <Badge variant={getStatusColor(selectedSupplier.statusColaborare)}>
                  {selectedSupplier.statusColaborare}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Informații Contact */}
              <Card className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Contact</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedSupplier.telefon}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedSupplier.email}
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedSupplier.adresa}
                    </span>
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Persoana de contact
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedSupplier.persoanaContact}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedSupplier.telefonContact}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Statistici Colaborare */}
              <Card className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Statistici Colaborare</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Valoare totală 2024:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedSupplier.valoareTotalaAn.toLocaleString()} RON
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Număr comenzi:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedSupplier.numarComenzi}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Ultima comandă:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(selectedSupplier.dataUltimaComanda).toLocaleDateString('ro-RO')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Valoare ultimă:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedSupplier.valoareUltimaComanda.toLocaleString()} RON
                    </span>
                  </div>
                </div>
              </Card>

              {/* Condiții Comerciale */}
              <Card className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Condiții Comerciale</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Timp mediu livrare:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedSupplier.timpMediuLivrare} zile
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Rata întârziere:</span>
                    <span className={`text-sm font-medium ${
                      selectedSupplier.rataIntarziere < 3 ? 'text-green-600' : 
                      selectedSupplier.rataIntarziere < 6 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {selectedSupplier.rataIntarziere}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Condiții plată:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedSupplier.conditiiPlata}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Discount acordat:</span>
                    <span className="text-sm font-medium text-green-600">
                      {selectedSupplier.discount}
                    </span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Produse Principale */}
            <Card className="p-4 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Produse Principale</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {selectedSupplier.produsePrincipale.map((produs, index) => (
                  <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {produs}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Certificări */}
            <Card className="p-4 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Certificări & Standarde</h3>
              <div className="flex flex-wrap gap-2">
                {selectedSupplier.certificari.map((certificare, index) => (
                  <Badge key={index} variant="secondary" className="px-3 py-1">
                    {certificare}
                  </Badge>
                ))}
              </div>
            </Card>

            {/* Note */}
            {selectedSupplier.note && (
              <Card className="p-4 mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Note & Observații</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedSupplier.note}
                </p>
              </Card>
            )}

            <div className="flex justify-end space-x-3">
              <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Istoric Comenzi
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Comandă Nouă
              </button>
              <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                Editează Furnizor
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SuppliersManagement;